<template>
  <Addition v-if="isAddition">
    <template #field>{{ change.field.name }}</template>
    <h4>
      {{ $t("message.common.category") }}:
      <span>{{ isUrlAttribute ? attributeName : attributeGroupName }}</span>
      <span v-if="isEnum || isRepeatedEnum"> - {{ attributeName }}</span>
    </h4>
    <GmbAttributeChangeItem
      :attribute="change.new_value"
      :change-type="change.type"
    ></GmbAttributeChangeItem>
  </Addition>
  <Removal v-else-if="isRemoval">
    <template #field>{{ change.field.name }}</template>
    <h4>
      {{ $t("message.common.category") }}:
      <span>{{ isUrlAttribute ? attributeName : attributeGroupName }}</span>
      <span v-if="isEnum || isRepeatedEnum"> - {{ attributeName }}</span>
    </h4>
    <GmbAttributeChangeItem
      :attribute="change.old_value"
      :change-type="change.type"
    ></GmbAttributeChangeItem>
  </Removal>
  <Change v-else-if="isChange" :vertical="isUrlAttribute">
    <template #field>{{ change.field.name }}</template>
    <h4 class="mb-2">
      {{ $t("message.common.category") }}:
      <span>{{ isUrlAttribute ? attributeName : attributeGroupName }}</span>
      <span v-if="isEnum || isRepeatedEnum"> - {{ attributeName }}</span>
    </h4>
    <template #old>
      <GmbAttributeChangeItem
        :attribute="change.old_value"
        :change-type="change.type"
      ></GmbAttributeChangeItem>
    </template>
    <template #new>
      <GmbAttributeChangeItem
        :attribute="change.new_value"
        :change-type="change.type"
      ></GmbAttributeChangeItem>
    </template>
  </Change>
</template>

<script>
import { EXTERNAL_CHANGES_TYPE } from "@/enums/external-changes";
import { GmbAttributeValueType } from "@/enums/gmb-attribute-value-type";

import Addition from "@/components/ExternalChanges/general/Addition.vue";
import Removal from "@/components/ExternalChanges/general/Removal.vue";
import Change from "@/components/ExternalChanges/general/Change.vue";
import GmbAttributeChangeItem from "@/components/ExternalChanges/helpers/GmbAttributeChangeItem";

export default {
  name: "AttributeChange",
  props: {
    change: {
      type: Object,
      required: true
    }
  },
  components: { Addition, Removal, Change, GmbAttributeChangeItem },

  computed: {
    currentLanguage() {
      return this.$i18n.locale;
    },
    isCurrentLanguageDifferentThanPl() {
      return this.currentLanguage !== "pl";
    },
    isAddition() {
      return this.change && this.change.type
        ? this.change.type === EXTERNAL_CHANGES_TYPE.ADDITION &&
            this.change.new_value
        : false;
    },
    isRemoval() {
      return this.change && this.change.type
        ? this.change.type === EXTERNAL_CHANGES_TYPE.REMOVAL &&
            this.change.old_value
        : false;
    },
    isChange() {
      return this.change && this.change.type
        ? this.change.type === EXTERNAL_CHANGES_TYPE.CHANGE &&
            this.change.new_value &&
            this.change.old_value
        : false;
    },
    attributeGroupName() {
      if (
        this.change &&
        this.change.old_value &&
        this.change.old_value.gmb_attribute &&
        this.change.old_value.gmb_attribute.group_display_name
      )
        return this.isCurrentLanguageDifferentThanPl &&
          this.change.old_value.gmb_attribute.group_display_name_en
          ? this.change.old_value.gmb_attribute.group_display_name_en
          : this.change.old_value.gmb_attribute.group_display_name;
      if (
        this.change &&
        this.change.new_value &&
        this.change.new_value.gmb_attribute &&
        this.change.new_value.gmb_attribute.group_display_name
      )
        return this.isCurrentLanguageDifferentThanPl &&
          this.change.new_value.gmb_attribute.group_display_name_en
          ? this.change.new_value.gmb_attribute.group_display_name_en
          : this.change.new_value.gmb_attribute.group_display_name;

      return "";
    },
    attributeName() {
      if (
        this.change &&
        this.change.old_value &&
        this.change.old_value.gmb_attribute &&
        this.change.old_value.gmb_attribute.display_name
      )
        return this.isCurrentLanguageDifferentThanPl &&
          this.change.old_value.gmb_attribute.display_name_en
          ? this.change.old_value.gmb_attribute.display_name_en
          : this.change.old_value.gmb_attribute.display_name;
      if (
        this.change &&
        this.change.new_value &&
        this.change.new_value.gmb_attribute &&
        this.change.new_value.gmb_attribute.display_name
      )
        return this.isCurrentLanguageDifferentThanPl &&
          this.change.new_value.gmb_attribute.display_name_en
          ? this.change.new_value.gmb_attribute.display_name_en
          : this.change.new_value.gmb_attribute.display_name;

      return "";
    },
    isEnum() {
      return (
        (this.change.new_value &&
          this.change.new_value.gmb_attribute &&
          this.change.new_value.gmb_attribute.value_type &&
          this.change.new_value.gmb_attribute.value_type ===
            GmbAttributeValueType.ENUM) ||
        (this.change.old_value &&
          this.change.old_value.gmb_attribute &&
          this.change.old_value.gmb_attribute.value_type &&
          this.change.old_value.gmb_attribute.value_type ===
            GmbAttributeValueType.ENUM) ||
        false
      );
    },
    isRepeatedEnum() {
      return (
        (this.change.new_value &&
          this.change.new_value.gmb_attribute &&
          this.change.new_value.gmb_attribute.value_type &&
          this.change.new_value.gmb_attribute.value_type ===
            GmbAttributeValueType.REPEATED_ENUM) ||
        (this.change.old_value &&
          this.change.old_value.gmb_attribute &&
          this.change.old_value.gmb_attribute.value_type &&
          this.change.old_value.gmb_attribute.value_type ===
            GmbAttributeValueType.REPEATED_ENUM) ||
        false
      );
    },
    isUrlAttribute() {
      return (
        (this.change.new_value &&
          this.change.new_value.gmb_attribute &&
          this.change.new_value.gmb_attribute.value_type &&
          this.change.new_value.gmb_attribute.value_type ===
            GmbAttributeValueType.URL) ||
        (this.change.old_value &&
          this.change.old_value.gmb_attribute &&
          this.change.old_value.gmb_attribute.value_type &&
          this.change.old_value.gmb_attribute.value_type ===
            GmbAttributeValueType.URL) ||
        false
      );
    }
  }
};
</script>

<style lang="scss" scoped>
h4 {
  @include font-size(14px);
  opacity: 0.6;
  font-weight: 400;

  span {
    font-weight: 500;
  }
}

.new-value {
  font-weight: bold;
}

.old-value {
  text-decoration: line-through;
}
</style>
